import React, { useRef, useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

interface AudioVisualizerProps {
  audioElement: HTMLAudioElement | null;
  isPlaying: boolean;
  className?: string;
  visualizerType?: 'bars' | 'wave' | 'spectrum' | 'retro';
}

// Global audio context and source management to handle multiple AudioVisualizer instances
const globalAudioState = {
  audioContext: null as AudioContext | null,
  analyser: null as AnalyserNode | null,
  source: null as MediaElementAudioSourceNode | null,
  connectedElement: null as HTMLAudioElement | null
};

export const AudioVisualizer: React.FC<AudioVisualizerProps> = ({ 
  audioElement, 
  isPlaying, 
  className,
  visualizerType = 'bars'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);

  // Helper function to get actual CSS color values
  const getCSSColor = (cssVar: string): string => {
    if (typeof window === 'undefined') return '#ffffff';
    
    const style = getComputedStyle(document.documentElement);
    const value = style.getPropertyValue(cssVar).trim();
    
    if (!value) return '#ffffff';
    
    // If it's already an HSL value, convert to a proper format
    if (value.includes(' ')) {
      const parts = value.split(' ');
      if (parts.length === 3) {
        return `hsl(${parts[0]}, ${parts[1]}, ${parts[2]})`;
      }
    }
    
    return value;
  };

  useEffect(() => {
    console.log('AudioVisualizer effect triggered - audioElement:', !!audioElement, 'isPlaying:', isPlaying, 'initialized:', isInitialized);
    
    // Initialize audio context only once when we get an audio element
    if (audioElement && !isInitialized) {
      console.log('Initializing audio context for visualizer');
      initializeAudioContext();
    }
  }, [audioElement]);

  // Separate effect for controlling visualization based on isPlaying
  useEffect(() => {
    if (isPlaying && globalAudioState.analyser && isInitialized) {
      console.log('Starting visualization');
      startVisualization();
    } else {
      console.log('Stopping visualization - isPlaying:', isPlaying, 'analyser:', !!globalAudioState.analyser, 'initialized:', isInitialized);
      stopVisualization();
    }

    return () => {
      stopVisualization();
    };
  }, [isPlaying, isInitialized, visualizerType]);

  // Cleanup on unmount - but don't close audio context to avoid breaking audio
  useEffect(() => {
    return () => {
      // Only stop visualization, don't close audio context
      stopVisualization();
    };
  }, []);

  const initializeAudioContext = async () => {
    if (!audioElement) return;

    try {
      console.log('Initializing audio context for visualizer');

      // Check if we already have this audio element connected
      if (globalAudioState.connectedElement === audioElement && globalAudioState.analyser) {
        console.log('Reusing existing audio context for same element');
        setIsInitialized(true);
        return;
      }

      // Create or reuse audio context
      if (!globalAudioState.audioContext || globalAudioState.audioContext.state === 'closed') {
        console.log('Creating new audio context...');
        const audioContext = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
        
        // Resume audio context if it's suspended (required for user interaction)
        if (audioContext.state === 'suspended') {
          console.log('Audio context suspended, resuming...');
          await audioContext.resume();
        }
        
        globalAudioState.audioContext = audioContext;
      }

      // Create or reuse analyser
      if (!globalAudioState.analyser) {
        const analyser = globalAudioState.audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        globalAudioState.analyser = analyser;
        console.log('Analyser created with fftSize:', analyser.fftSize);
      }

      // Create source only if we don't have one for this element yet
      if (!globalAudioState.source || globalAudioState.connectedElement !== audioElement) {
        try {
          const source = globalAudioState.audioContext.createMediaElementSource(audioElement);
          globalAudioState.source = source;
          globalAudioState.connectedElement = audioElement;
          source.connect(globalAudioState.analyser);
          // Connect analyser to destination so audio is heard
          globalAudioState.analyser.connect(globalAudioState.audioContext.destination);
          console.log('Audio source created and connected successfully');
        } catch (error) {
          if (error instanceof Error && error.name === 'InvalidStateError') {
            console.log('Audio element already connected - reusing existing connection');
            // The audio element is already connected, which is expected
            globalAudioState.connectedElement = audioElement;
          } else {
            throw error;
          }
        }
      }
      
      setIsInitialized(true);
      console.log('Audio context initialized successfully');
    } catch (error) {
      console.error('Error initializing audio context:', error);
    }
  };

  const startVisualization = () => {
    if (!globalAudioState.analyser || !canvasRef.current) {
      console.log('Cannot start visualization - missing analyser or canvas');
      console.log('Analyser exists:', !!globalAudioState.analyser);
      console.log('Canvas exists:', !!canvasRef.current);
      return;
    }

    console.log('Starting animation loop');
    const animate = () => {
      if (!isPlaying) {
        console.log('Animation stopped - not playing');
        return;
      }
      
      drawVisualization();
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
  };

  const stopVisualization = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
    
    // Clear canvas
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  };

  const drawVisualization = () => {
    const canvas = canvasRef.current;
    const analyser = globalAudioState.analyser;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteFrequencyData(dataArray);
    
    const hasAudioData = Math.max(...dataArray) > 0;
    if (!hasAudioData) {
      // Simple test pattern
      ctx.fillStyle = '#333';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      return;
    }

    const time = Date.now() * 0.001;

    switch (visualizerType) {
      case 'bars':
        drawWinampBars(ctx, dataArray, canvas.width, canvas.height, time);
        break;
      case 'wave':
        drawPsychedelicWave(ctx, dataArray, canvas.width, canvas.height, time);
        break;
      case 'spectrum':
        drawRadialSpectrum(ctx, dataArray, canvas.width, canvas.height, time);
        break;
      case 'retro':
        drawRetroOscilloscope(ctx, dataArray, canvas.width, canvas.height, time);
        break;
    }
  };

  // Epic Winamp-style bar visualization with neon glow
  const drawWinampBars = (ctx: CanvasRenderingContext2D, dataArray: Uint8Array, width: number, height: number, time: number) => {
    // Clear with trail effect
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.fillRect(0, 0, width, height);
    
    const barWidth = Math.max(2, width / dataArray.length * 2);
    let x = 0;
    
    for (let i = 0; i < dataArray.length; i++) {
      const barHeight = (dataArray[i] / 255) * height * 0.8;
      
      // Rainbow color cycling
      const hue = (i * 3 + time * 60) % 360;
      const brightness = 50 + (dataArray[i] / 255) * 30;
      
      // Neon glow effect
      ctx.shadowBlur = 15;
      ctx.shadowColor = `hsl(${hue}, 100%, 50%)`;
      
      // Main bar with gradient
      const gradient = ctx.createLinearGradient(0, height, 0, height - barHeight);
      gradient.addColorStop(0, `hsl(${hue}, 100%, 30%)`);
      gradient.addColorStop(0.5, `hsl(${hue}, 100%, ${brightness}%)`);
      gradient.addColorStop(1, `hsl(${hue + 30}, 100%, 70%)`);
      
      ctx.fillStyle = gradient;
      ctx.fillRect(x, height - barHeight, barWidth - 2, barHeight);
      
      // Add reflection at bottom
      ctx.save();
      ctx.globalAlpha = 0.3;
      ctx.scale(1, -1);
      ctx.translate(0, -height);
      ctx.fillRect(x, height - barHeight * 0.5, barWidth - 2, barHeight * 0.5);
      ctx.restore();
      
      x += barWidth;
    }
    ctx.shadowBlur = 0;
  };

  // Psychedelic wave with particles
  const drawPsychedelicWave = (ctx: CanvasRenderingContext2D, dataArray: Uint8Array, width: number, height: number, time: number) => {
    // Trail effect
    ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
    ctx.fillRect(0, 0, width, height);
    
    const centerY = height / 2;
    
    // Multiple waves with different phases
    for (let wave = 0; wave < 3; wave++) {
      ctx.lineWidth = 4 - wave;
      ctx.beginPath();
      
      const phase = wave * Math.PI / 3;
      const amplitude = (1 - wave * 0.3);
      
      for (let i = 0; i < dataArray.length; i++) {
        const x = (i / dataArray.length) * width;
        const audioValue = dataArray[i] / 255;
        const y = centerY + Math.sin(i * 0.02 + time * 2 + phase) * audioValue * height * 0.3 * amplitude;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      
      // Psychedelic gradient
      const gradient = ctx.createLinearGradient(0, 0, width, 0);
      gradient.addColorStop(0, `hsl(${(time * 100 + wave * 120) % 360}, 100%, 60%)`);
      gradient.addColorStop(0.5, `hsl(${(time * 150 + wave * 120 + 180) % 360}, 100%, 70%)`);
      gradient.addColorStop(1, `hsl(${(time * 80 + wave * 120 + 240) % 360}, 100%, 50%)`);
      
      ctx.strokeStyle = gradient;
      ctx.shadowBlur = 10;
      ctx.shadowColor = `hsl(${(time * 100 + wave * 120) % 360}, 100%, 50%)`;
      ctx.stroke();
    }
    
    // Floating particles on peaks
    for (let i = 0; i < dataArray.length; i += 8) {
      if (dataArray[i] > 180) {
        const x = (i / dataArray.length) * width;
        const y = centerY + Math.sin(i * 0.02 + time * 2) * (dataArray[i] / 255) * height * 0.3;
        
        ctx.beginPath();
        ctx.arc(x, y, Math.random() * 4 + 2, 0, Math.PI * 2);
        ctx.fillStyle = `hsl(${(time * 200 + i * 10) % 360}, 100%, 70%)`;
        ctx.shadowBlur = 8;
        ctx.fill();
      }
    }
    ctx.shadowBlur = 0;
  };

  // 3D rotating spectrum
  const drawRadialSpectrum = (ctx: CanvasRenderingContext2D, dataArray: Uint8Array, width: number, height: number, time: number) => {
    // Dark background with plasma effect
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.fillRect(0, 0, width, height);
    
    const centerX = width / 2;
    const centerY = height / 2;
    const maxRadius = Math.min(width, height) / 3;
    
    // Plasma background
    for (let x = 0; x < width; x += 8) {
      for (let y = 0; y < height; y += 8) {
        const dist = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
        const value = Math.sin(dist * 0.03 + time * 2) + Math.sin(x * 0.01 + time) + Math.sin(y * 0.01 + time * 1.5);
        const hue = (value * 60 + time * 100) % 360;
        const alpha = (Math.sin(value + time) + 1) * 0.03;
        
        ctx.fillStyle = `hsla(${hue}, 100%, 50%, ${alpha})`;
        ctx.fillRect(x, y, 8, 8);
      }
    }
    
    // Radial spectrum
    for (let i = 0; i < dataArray.length; i++) {
      const angle = (i / dataArray.length) * Math.PI * 2 + time * 0.5;
      const amplitude = (dataArray[i] / 255) * maxRadius * 0.8;
      
      const innerRadius = 30 + Math.sin(time * 2) * 10;
      const x1 = centerX + Math.cos(angle) * innerRadius;
      const y1 = centerY + Math.sin(angle) * innerRadius;
      const x2 = centerX + Math.cos(angle) * (innerRadius + amplitude);
      const y2 = centerY + Math.sin(angle) * (innerRadius + amplitude);
      
      const hue = (angle * 180 / Math.PI + time * 120) % 360;
      
      // Thick line with glow
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = `hsl(${hue}, 100%, 60%)`;
      ctx.shadowBlur = 12;
      ctx.shadowColor = `hsl(${hue}, 100%, 50%)`;
      ctx.stroke();
      
      // Add dots at tips for extra sparkle
      if (amplitude > maxRadius * 0.3) {
        ctx.beginPath();
        ctx.arc(x2, y2, 3, 0, Math.PI * 2);
        ctx.fillStyle = `hsl(${hue + 60}, 100%, 80%)`;
        ctx.fill();
      }
    }
    
    // Rotating center burst
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(time * 1.5);
    
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      const x = Math.cos(angle) * 15;
      const y = Math.sin(angle) * 15;
      
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, Math.PI * 2);
      ctx.fillStyle = `hsl(${(time * 150 + i * 30) % 360}, 100%, 70%)`;
      ctx.fill();
    }
    ctx.restore();
    ctx.shadowBlur = 0;
  };

  // Retro oscilloscope with scan lines
  const drawRetroOscilloscope = (ctx: CanvasRenderingContext2D, dataArray: Uint8Array, width: number, height: number, time: number) => {
    // CRT-style background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
    ctx.fillRect(0, 0, width, height);
    
    // Grid pattern
    ctx.strokeStyle = 'rgba(0, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    
    // Grid lines
    const gridSize = 25;
    for (let i = 0; i < width; i += gridSize) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
    
    for (let i = 0; i < height; i += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(width, i);
      ctx.stroke();
    }
    
    // Multiple oscilloscope traces
    const traces = [
      { color: 'cyan', offset: 0, amplitude: 0.8 },
      { color: 'lime', offset: Math.PI / 2, amplitude: 0.6 },
      { color: 'magenta', offset: Math.PI, amplitude: 0.4 }
    ];
    
    traces.forEach((trace, traceIndex) => {
      ctx.beginPath();
      ctx.lineWidth = 2;
      ctx.strokeStyle = trace.color;
      ctx.shadowColor = trace.color;
      ctx.shadowBlur = 8;
      
      const centerY = height / 2 + Math.sin(time + trace.offset) * 20;
      
      for (let i = 0; i < dataArray.length; i++) {
        const x = (i / dataArray.length) * width;
        const audioValue = dataArray[i] / 255;
        const y = centerY + Math.sin(i * 0.1 + time * 3 + trace.offset) * audioValue * height * 0.2 * trace.amplitude;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    });
    
    // Scanning beam effect
    const scanX = (time * 100) % width;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(scanX, 0);
    ctx.lineTo(scanX, height);
    ctx.stroke();
    
    // CRT scan lines
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    for (let y = 0; y < height; y += 3) {
      ctx.fillRect(0, y, width, 1);
    }
    
    // Screen edge glow
    const gradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, Math.max(width, height)/2);
    gradient.addColorStop(0, 'rgba(0, 255, 255, 0)');
    gradient.addColorStop(0.8, 'rgba(0, 255, 255, 0)');
    gradient.addColorStop(1, 'rgba(0, 255, 255, 0.2)');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    ctx.shadowBlur = 0;
  };

  return (
    <div className={cn("relative overflow-hidden", className)}>
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ imageRendering: 'pixelated' }}
      />
      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/50">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded bg-muted/50 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-muted-foreground/30 rounded" />
            </div>
            <p className="text-sm">Play music for visualizations</p>
          </div>
        </div>
      )}
    </div>
  );
};